/**
 * i18next bootstrap. Imported for its side effect (`./i18n`) before the app
 * renders so the active language is resolved on first paint. Components consume
 * it via `useTranslation(ns)`; there is no `<I18nextProvider>` because
 * `initReactI18next` registers this singleton globally.
 *
 * `i18nReady` is the init promise — App holds the native splash until it settles
 * so the first frame is already localized (premortem R6: no English→French flip).
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import ICU from 'i18next-icu';
import LanguageDetector from 'i18next-browser-languagedetector';

import {
  BASE_RESOURCES,
  DEFAULT_NAMESPACE,
  FALLBACK_LANGUAGE,
  I18N_NAMESPACES,
  SUPPORTED_LNGS,
  SUPPORTED_LNGS_WITH_PSEUDO,
  buildDevResources,
  loadLanguageCatalog,
} from './lib/i18n/resources';

const isDev = import.meta.env.DEV;

// Local alias: i18next's builder API is `.use(...).init(...)`, but `.use`
// collides with i18next's named `use` export, which import/no-named-as-default-member
// flags on direct default-import member access. The alias is the documented API.
const i18next = i18n;

export const i18nReady = i18next
  .use(new ICU())
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: isDev ? buildDevResources() : BASE_RESOURCES,
    supportedLngs: isDev ? SUPPORTED_LNGS_WITH_PSEUDO : SUPPORTED_LNGS,
    fallbackLng: FALLBACK_LANGUAGE,
    defaultNS: DEFAULT_NAMESPACE,
    ns: [...I18N_NAMESPACES],
    returnNull: false,
    // React escapes interpolated values already; double-escaping mangles output.
    // Untrusted (constituent) HTML must NEVER be interpolated through t() — the
    // DOMPurify boundary is the only sanctioned path for that.
    interpolation: { escapeValue: false },
    // No Suspense: every catalog is resident in memory before a `t()` consumer
    // renders, and the app has no i18n Suspense boundary — a returning component
    // must render with the (already-resolved) active language, not throw a
    // promise. Non-`en` catalogs are fetched, but only inside the `i18nReady`
    // window below, never underneath a rendering component.
    react: { useSuspense: false },
    detection: {
      // localStorage first so a returning user's last language seeds before the
      // first paint; navigator is the cold-start guess. Resolved profile/org
      // preference reconciles async via useLanguageSync once auth is known.
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
    },
  })
  // The detected language is only known after init, so its catalog is fetched
  // here rather than passed to `resources`. App gates the splash on this promise
  // (premortem R6), so the fetch lands before the first frame and a French user
  // never sees an English→French flip.
  .then(async (t) => {
    await ensureLanguageCatalog(i18n.language);
    return t;
  });

/**
 * Make `language`'s catalog resident, if it is not already. Safe to call for any
 * language and any number of times; `en` and the pseudo-locale resolve without a
 * fetch.
 */
export async function ensureLanguageCatalog(language: string): Promise<void> {
  const catalog = await loadLanguageCatalog(language);
  if (!catalog) {
    return;
  }
  const base = language.split('-')[0];
  for (const [namespace, strings] of Object.entries(catalog)) {
    // `deep`/`overwrite` false: a namespace already added by an earlier call is
    // the same module-cached object, so re-adding would be pure work.
    i18n.addResourceBundle(base, namespace, strings, false, false);
  }
}

/**
 * The only sanctioned way to change language.
 *
 * MUST be used instead of `i18n.changeLanguage`. Only `en` is bundled eagerly,
 * so a bare `changeLanguage('fr')` resolves happily against an empty catalog and
 * renders the `en` fallback until the chunk arrives — a silent, and on a slow
 * connection lasting, English→French flip. Loading first closes that window.
 * Enforced by `scripts/check-i18n-language-switch.sh`.
 */
export async function setLanguage(language: string): Promise<void> {
  await ensureLanguageCatalog(language);
  // `i18next`, not `i18n`: i18next also exports a named `changeLanguage`, which
  // import/no-named-as-default-member flags on direct default-import member
  // access. Same alias the builder chain above uses.
  await i18next.changeLanguage(language);
}

export default i18n;
